import React, { useRef, useEffect } from "react";
import { setupCanvas } from "./canvasUtils";

// ============================================
// Chapter 30  -  Proposal Writing for BL4S
// ============================================

/** 30.1 – Structure of a Physics Proposal */
export const ProposalStructure = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 380;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Structure of a BL4S Proposal", W / 2, 24);

    // Sections
    const sections = [
      { num: "1", title: "Abstract", desc: "1 paragraph: what, why, how, expected outcome", color: "#EF4444", tip: "Reviewers read this first  -  make it compelling" },
      { num: "2", title: "Physics Motivation", desc: "What physics do you want to study? Why?", color: "#F59E0B", tip: "Connect to real HEP questions" },
      { num: "3", title: "Experimental Setup", desc: "Detectors, beam, trigger, data flow diagram", color: "#3B82F6", tip: "Show you understand T9 capabilities" },
      { num: "4", title: "Measurement Plan", desc: "What will you measure? How will you analyze?", color: "#8B5CF6", tip: "Be specific: histograms, fits, cuts" },
      { num: "5", title: "Expected Results", desc: "Simulations, estimates, sensitivity studies", color: "#10B981", tip: "Show feasibility  -  you only get 10 days!" },
      { num: "6", title: "Team & Outreach", desc: "Who you are, mentor, communication plan", color: "#64748B", tip: "BL4S values education and outreach" },
    ];
    sections.forEach((s, i) => {
      const y = 45 + i * 53;
      ctx.fillStyle = s.color + "10";
      ctx.beginPath(); ctx.roundRect(20, y, 520, 46, 8); ctx.fill();
      ctx.strokeStyle = s.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(20, y, 520, 46, 8); ctx.stroke();
      ctx.fillStyle = s.color; ctx.font = "bold 16px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(s.num, 32, y + 20);
      ctx.font = "bold 12px Inter,sans-serif"; ctx.fillText(s.title, 55, y + 20);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(s.desc, 55, y + 36);
      ctx.fillStyle = s.color; ctx.font = "italic 10px Inter,sans-serif";
      ctx.textAlign = "right"; ctx.fillText(s.tip, 530, y + 20);
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Total length typically 10-15 pages + figures + bibliography", W / 2, 370);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 30.2 – Choosing a Physics Topic */
export const ChoosingPhysicsTopic = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Choosing Your BL4S Physics Topic", W / 2, 24);

    // Decision criteria
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Key Selection Criteria", W / 2, 52);
    const criteria = [
      { label: "Feasibility", desc: "Can it be done with T9 beam (0.5-10 GeV/c π/K/p/e)?", icon: "✓", color: "#10B981" },
      { label: "Time", desc: "Only ~10 days of beam time  -  keep scope realistic", icon: "⏱", color: "#F59E0B" },
      { label: "Interest", desc: "Does it excite your team? Passion shows in proposals", icon: "★", color: "#EF4444" },
      { label: "Originality", desc: "Novel approach or measurement? Stand out from others", icon: "�-�", color: "#8B5CF6" },
      { label: "Education", desc: "Will you learn key physics/detector concepts?", icon: "�-�", color: "#3B82F6" },
    ];
    criteria.forEach((cr, i) => {
      const y = 68 + i * 28;
      ctx.fillStyle = cr.color; ctx.font = "bold 14px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(cr.icon, 40, y);
      ctx.font = "bold 12px Inter,sans-serif"; ctx.fillText(cr.label, 65, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(cr.desc, 165, y);
    });
    ctx.textAlign = "center";

    // Example topics from past winners
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(30, 215, 500, 130, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 215, 500, 130, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Example Topics from Past BL4S Winners", W / 2, 235);
    const topics = [
      "Measuring the speed of light using TOF and known distances",
      "Cherenkov radiation threshold: identify pions vs kaons",
      "Cosmic ray muon lifetime measurement (without beam!)",
      "Proton-proton elastic scattering cross-section",
      "Electromagnetic shower profile in lead-glass calorimeter",
      "Particle identification using dE/dx in scintillators",
    ];
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    topics.forEach((t, i) => ctx.fillText(t, W / 2, 255 + i * 15));
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 30.3 – Writing the Experimental Setup Section */
export const ExperimentalSetupWriting = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Writing the Experimental Setup", W / 2, 24);

    // Must include
    ctx.fillStyle = "#D1FAE5";
    ctx.beginPath(); ctx.roundRect(20, 45, 250, 170, 10); ctx.fill();
    ctx.strokeStyle = "#10B981"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(20, 45, 250, 170, 10); ctx.stroke();
    ctx.fillStyle = "#065F46"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Must Include", 145, 65);
    const musts = [
      "Detector layout diagram",
      "Beam momentum setting",
      "Trigger logic (AND/OR)",
      "Data acquisition (DAQ)",
      "Detector specifications",
      "Signal path: detector → ADC",
      "Calibration plan",
    ];
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    musts.forEach((m, i) => ctx.fillText(m, 145, 85 + i * 17));

    // Diagram example
    ctx.fillStyle = "#EDE9FE";
    ctx.beginPath(); ctx.roundRect(290, 45, 250, 170, 10); ctx.fill();
    ctx.strokeStyle = "#8B5CF6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(290, 45, 250, 170, 10); ctx.stroke();
    ctx.fillStyle = "#6D28D9"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Setup Diagram Tips", 415, 65);
    const tips = [
      "Show beam direction (left→right)",
      "Label ALL detectors with names",
      "Mark distances in mm/cm",
      "Show trigger connections",
      "Include coordinate axes (x,y,z)",
      "Use consistent scale",
      "Indicate beam spot size",
    ];
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    tips.forEach((t, i) => ctx.fillText(t, 415, 85 + i * 17));

    // Common mistakes
    ctx.fillStyle = "#FEE2E2";
    ctx.beginPath(); ctx.roundRect(20, 230, 520, 120, 10); ctx.fill();
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(20, 230, 520, 120, 10); ctx.stroke();
    ctx.fillStyle = "#991B1B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Common Mistakes to Avoid", W / 2, 250);
    const mistakes = [
      "Vague detector descriptions ('we use a detector to measure particles')",
      "No trigger logic specified  -  reviewers want to see concrete design",
      "Impossible beam requests (momentum outside T9 range)",
      "No calibration plan  -  how do you convert ADC counts to physics?",
      "Missing error analysis  -  always estimate expected uncertainties",
    ];
    ctx.fillStyle = "#EF4444"; ctx.font = "11px Inter,sans-serif";
    mistakes.forEach((m, i) => ctx.fillText(m, W / 2, 270 + i * 16));
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 30.4 – Simulations and Feasibility Studies */
export const SimulationFeasibility = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Simulations & Feasibility Studies", W / 2, 24);

    // Why simulate
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 50, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 50, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Simulations prove your experiment will WORK before you get beam time", W / 2, 65);
    ctx.fillStyle = "#3B82F6"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("This is what separates strong proposals from weak ones!", W / 2, 82);

    // Tools
    const tools = [
      { tool: "Geant4", desc: "Full detector simulation: particle transport, interactions, energy deposits", color: "#EF4444" },
      { tool: "ROOT", desc: "Data analysis framework: histograms, fits, statistical tests", color: "#3B82F6" },
      { tool: "Python", desc: "Monte Carlo generators, quick calculations, plotting (matplotlib)", color: "#10B981" },
      { tool: "Excel/Sheets", desc: "Quick back-of-envelope estimates, rate calculations", color: "#F59E0B" },
    ];
    tools.forEach((t, i) => {
      const y = 105 + i * 25;
      ctx.fillStyle = t.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(t.tool, 40, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(t.desc, 140, y);
    });
    ctx.textAlign = "center";

    // What to show
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("What to Include in Feasibility Section", W / 2, 220);
    const show = [
      { item: "Expected signal", desc: "Simulated mass peak, TOF distribution, etc.", color: "#3B82F6" },
      { item: "Background estimate", desc: "What could fake your signal? How much?", color: "#8B5CF6" },
      { item: "Signal-to-noise", desc: "S/√(S+B)  -  can you see the signal above noise?", color: "#EF4444" },
      { item: "Statistics needed", desc: "How many events? How long to accumulate?", color: "#10B981" },
      { item: "Systematic effects", desc: "Detector resolution, alignment, calibration impact", color: "#F59E0B" },
    ];
    show.forEach((s, i) => {
      const y = 240 + i * 22;
      ctx.fillStyle = s.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(s.item, 40, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(s.desc, 200, y);
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Even simple Python simulations impress reviewers more than no simulations!", W / 2, 353);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 30.5 – Data Analysis Plan */
export const DataAnalysisPlan = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Data Analysis Plan", W / 2, 24);

    // Analysis chain
    const chain = [
      { step: "Raw Data", desc: "ADC/TDC values\nfrom DAQ", color: "#EF4444" },
      { step: "Calibration", desc: "Convert counts\nto physical units", color: "#F59E0B" },
      { step: "Selection", desc: "Apply quality\ncuts (trigger, PID)", color: "#3B82F6" },
      { step: "Histograms", desc: "Fill distributions\n(mass, energy, TOF)", color: "#8B5CF6" },
      { step: "Fit & Extract", desc: "Fit signal model\nextract parameters", color: "#10B981" },
    ];
    let xPos = 15;
    chain.forEach((ch, i) => {
      const w = 95, y = 45, h = 75;
      ctx.fillStyle = ch.color + "12";
      ctx.beginPath(); ctx.roundRect(xPos, y, w, h, 8); ctx.fill();
      ctx.strokeStyle = ch.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(xPos, y, w, h, 8); ctx.stroke();
      ctx.fillStyle = ch.color; ctx.font = "bold 10px Inter,sans-serif";
      ctx.fillText(ch.step, xPos + w / 2, y + 16);
      ctx.fillStyle = "#475569"; ctx.font = "9px Inter,sans-serif";
      ch.desc.split("\n").forEach((ln, li) => ctx.fillText(ln, xPos + w / 2, y + 34 + li * 12));
      if (i < chain.length - 1) {
        ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(xPos + w + 2, y + h / 2); ctx.lineTo(xPos + w + 12, y + h / 2); ctx.stroke();
      }
      xPos += w + 14;
    });

    // Key analysis techniques
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Key Analysis Techniques for BL4S", W / 2, 145);
    const techniques = [
      { tech: "Histogram fitting", desc: "Fit Gaussian/Breit-Wigner to peak → extract mass, width", color: "#3B82F6" },
      { tech: "Cut-based selection", desc: "pT > X, |eta| < Y, timing window → separate signal from bg", color: "#8B5CF6" },
      { tech: "Sideband estimation", desc: "Estimate background from signal-free regions of distribution", color: "#EF4444" },
      { tech: "Efficiency correction", desc: "N_true = N_measured / ε  (geometric, trigger, reco efficiency)", color: "#10B981" },
      { tech: "Error propagation", desc: "σ_f = √(Σ (∂f/∂xi)² σ²_xi)   -  combine stat + systematic", color: "#F59E0B" },
    ];
    techniques.forEach((t, i) => {
      const y = 165 + i * 25;
      ctx.fillStyle = t.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(t.tech, 40, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(t.desc, 200, y);
    });
    ctx.textAlign = "center";

    // Pro tip
    ctx.fillStyle = "#D1FAE5";
    ctx.beginPath(); ctx.roundRect(30, 300, 500, 50, 10); ctx.fill();
    ctx.strokeStyle = "#10B981"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 300, 500, 50, 10); ctx.stroke();
    ctx.fillStyle = "#065F46"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Pro Tip: Show Example Plots", W / 2, 320);
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Include simulated histograms in your proposal  -  show what you EXPECT to see", W / 2, 340);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 30.6 – Outreach and Communication Plan */
export const OutreachCommunication = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Outreach & Communication Plan", W / 2, 24);

    // Why outreach matters
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 45, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 45, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("BL4S is an EDUCATION program  -  outreach is heavily weighted in judging!", W / 2, 62);
    ctx.fillStyle = "#3B82F6"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Show how you will share your experience with your school, community, and beyond", W / 2, 80);

    // Outreach channels
    const channels = [
      { ch: "School presentations", desc: "Present to physics classes, science club, school assembly", icon: "🏫", color: "#3B82F6" },
      { ch: "Social media", desc: "Blog, Instagram, YouTube documenting your BL4S journey", icon: "📱", color: "#8B5CF6" },
      { ch: "Local media", desc: "Newspaper article, TV interview  -  local students going to CERN!", icon: "📰", color: "#EF4444" },
      { ch: "Science fairs", desc: "Present at regional/national science fairs or conferences", icon: "🔬", color: "#10B981" },
      { ch: "Website/blog", desc: "Document the full project: preparation → CERN → results", icon: "💻", color: "#F59E0B" },
    ];
    channels.forEach((c2, i) => {
      const y = 105 + i * 30;
      ctx.fillStyle = c2.color + "10";
      ctx.beginPath(); ctx.roundRect(30, y, 500, 24, 6); ctx.fill();
      ctx.strokeStyle = c2.color; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.roundRect(30, y, 500, 24, 6); ctx.stroke();
      ctx.fillStyle = c2.color; ctx.font = "bold 12px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(`${c2.icon} ${c2.ch}`, 42, y + 17);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(c2.desc, 220, y + 17);
    });
    ctx.textAlign = "center";

    // Timeline
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath(); ctx.roundRect(30, 265, 500, 65, 10); ctx.fill();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 265, 500, 65, 10); ctx.stroke();
    ctx.fillStyle = "#92400E"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Outreach Timeline", W / 2, 283);
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Before CERN: preparation blog posts, school announcement", W / 2, 300);
    ctx.fillText("At CERN: daily updates, photos, short videos from the lab", W / 2, 315);
    ctx.fillText("After CERN: results presentation, final report, inspire next teams!", W / 2, 327);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 30.7 – Reviewing Past Winning Proposals */
export const PastWinningProposals = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 380;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Learning from Past BL4S Winners", W / 2, 24);

    // What winners do well
    ctx.fillStyle = "#D1FAE5";
    ctx.beginPath(); ctx.roundRect(20, 45, 250, 160, 10); ctx.fill();
    ctx.strokeStyle = "#10B981"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(20, 45, 250, 160, 10); ctx.stroke();
    ctx.fillStyle = "#065F46"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("What Winners Do Well", 145, 65);
    const goods = [
      "Clear, focused physics question",
      "Detailed detector setup diagram",
      "Realistic timeline for beam time",
      "Simulations backing up claims",
      "Strong outreach plan",
      "Team roles well-defined",
      "Good scientific writing style",
    ];
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    goods.forEach((g, i) => ctx.fillText(g, 145, 85 + i * 16));

    // Common weaknesses
    ctx.fillStyle = "#FEE2E2";
    ctx.beginPath(); ctx.roundRect(290, 45, 250, 160, 10); ctx.fill();
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(290, 45, 250, 160, 10); ctx.stroke();
    ctx.fillStyle = "#991B1B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Common Weaknesses", 415, 65);
    const bads = [
      "Too ambitious for 10 days",
      "Vague analysis plan",
      "No error estimates",
      "Copy-paste from textbooks",
      "No simulation results shown",
      "Outreach as afterthought",
      "Poor or no figures",
    ];
    ctx.fillStyle = "#EF4444"; ctx.font = "11px Inter,sans-serif";
    bads.forEach((b, i) => ctx.fillText(b, 415, 85 + i * 16));

    // Scoring criteria
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Typical Evaluation Criteria", W / 2, 225);
    const scoring = [
      { criterion: "Scientific Quality", weight: "30%", desc: "Physics motivation, originality, depth", color: "#3B82F6" },
      { criterion: "Feasibility", weight: "25%", desc: "Can it work at T9 in ~10 days?", color: "#10B981" },
      { criterion: "Experimental Design", weight: "20%", desc: "Setup, trigger, DAQ, analysis plan", color: "#8B5CF6" },
      { criterion: "Outreach Plan", weight: "15%", desc: "Communication, education impact", color: "#F59E0B" },
      { criterion: "Presentation", weight: "10%", desc: "Writing quality, figures, clarity", color: "#EF4444" },
    ];
    scoring.forEach((sc, i) => {
      const y = 245 + i * 24;
      ctx.fillStyle = sc.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(sc.criterion, 40, y);
      ctx.font = "bold 11px Inter,sans-serif"; ctx.fillText(sc.weight, 200, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(sc.desc, 250, y);
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Read past winning proposals on the BL4S website for inspiration!", W / 2, 370);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};
